import type { PromptTemplate } from "@/api/prompts";

export type PromptCategoryFilter = "all" | string;

export const CATEGORY_COLORS: Record<string, string> = {
  "opportunity-research": "bg-sky-500/10 text-sky-400",
  "opportunity-proposal": "bg-emerald-500/10 text-emerald-400",
  "opportunity-qualification": "bg-amber-500/10 text-amber-400",
  "opportunity-demo": "bg-violet-500/10 text-violet-400",
  "opportunity-discovery": "bg-rose-500/10 text-rose-400",
  "company-research": "bg-cyan-500/10 text-cyan-400",
  "opportunity-monitor": "bg-indigo-500/10 text-indigo-400",
  "opportunity-negotiation": "bg-orange-500/10 text-orange-400",
  "opportunity-reflection": "bg-fuchsia-500/10 text-fuchsia-400",
  "narrative-ingest": "bg-teal-500/10 text-teal-400",
};

export function categoryLabel(category: string): string {
  return category
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function categoryColor(category: string): string {
  return CATEGORY_COLORS[category] ?? "bg-muted text-muted-foreground";
}

export function promptCategoryTabs(prompts: PromptTemplate[]): PromptCategoryFilter[] {
  const categories = new Set<string>();
  for (const p of filterPrompts(prompts, { hideInactive: true })) {
    if (p.category) categories.add(p.category);
  }
  return ["all", ...Array.from(categories).sort()];
}

export function promptRequirementSummary(prompt: PromptTemplate): string {
  const parts: string[] = [];
  if (prompt.requiresCv) parts.push("Narrative");
  if (prompt.requiresProfile) parts.push("Profile");
  if (prompt.requiresMemory) parts.push("Memory");
  if (prompt.requiresDedup) parts.push("Dedup");
  return parts.length > 0 ? parts.join(" · ") : "No context required";
}

export interface PromptFilterOptions {
  search?: string;
  category?: PromptCategoryFilter;
  /** When true (default), hide inactive prompts. */
  hideInactive?: boolean;
}

export function filterPrompts(
  prompts: PromptTemplate[],
  { search = "", category = "all", hideInactive = true }: PromptFilterOptions,
): PromptTemplate[] {
  const q = search.trim().toLowerCase();

  return prompts.filter((p) => {
    if (hideInactive && p.isActive === false) return false;
    if (category !== "all" && p.category !== category) return false;
    if (q) {
      return (
        p.name.toLowerCase().includes(q) ||
        p.kind.toLowerCase().includes(q) ||
        (p.description?.toLowerCase().includes(q) ?? false) ||
        (p.category?.toLowerCase().includes(q) ?? false)
      );
    }
    return true;
  });
}

const PARSE_PATTERN_LABELS: Record<string, string> = {
  verdict: "Verdict line",
  "action-envelope": "Action envelope",
  "offer-envelope": "Lead envelope",
  "cv-envelope": "Narrative envelope",
  json: "JSON",
};

export function parsePatternLabel(pattern: string): string {
  return PARSE_PATTERN_LABELS[pattern] ?? pattern;
}

export function formatPromptDate(iso?: string): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export function promptContextRequirements(prompt: PromptTemplate): string[] {
  const flags: string[] = [];
  if (prompt.requiresCv) flags.push("Narrative");
  if (prompt.requiresProfile) flags.push("Profile");
  if (prompt.requiresMemory) flags.push("Memory");
  if (prompt.requiresDedup) flags.push("Dedup");
  return flags;
}
